import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { cn } from '../../utls/utils';

interface ModalPortalProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  panelClassName?: string;
}

export const ModalPortal: React.FC<ModalPortalProps> = ({ 
  children, 
  isOpen, 
  onClose,
  panelClassName,
}) => {
  const [portalElement, setPortalElement] = useState<HTMLElement | null>(null);
  
  useEffect(() => {
    // Look for existing modal-root or create it
    let element = document.getElementById('modal-root');
    
    if (!element) {
      element = document.createElement('div');
      element.id = 'modal-root';
      document.body.appendChild(element);
    }
    
    setPortalElement(element);
    
    // Add listener to close modal on escape key
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscKey);
    
    // Prevent scrolling when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      
      // Restore scrolling when component unmounts
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);
  
  if (!isOpen || !portalElement) return null;
  
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-12 pb-6">
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={cn(
          "relative z-50 mx-auto max-h-[85vh] w-full max-w-3xl overflow-hidden rounded-lg bg-white p-6 shadow-xl",
          panelClassName
        )}
      >
        {children}
      </div>
    </div>,
    portalElement
  );
};
